// Shared presentational pieces for attachment UI — the list (items + pending
// rows) and the drop-zone — driven by the useAttachments hook. Used by both
// AttachmentsSection (task case, stacked) and the project 3-card layout.

import { useState } from "preact/hooks";
import { Download, Eye, Paperclip, Trash2 } from "lucide-preact";
import type { AttachmentMeta } from "@juuplan/shared";
import { useMe } from "../../lib/auth";
import {
  AttachmentPreviewModal,
  OFFICE_PREVIEWABLE,
} from "../AttachmentPreviewModal";
import { PREVIEWABLE, prettyBytes, relTime } from "./useAttachments";
import type { PendingUpload, UseAttachments } from "./useAttachments";

// File-type accept attribute shared by every picker. Must mirror the
// server's allowlist (apps/server/src/routes/attachments.ts) — anything
// missing here is still uploadable via drag & drop, but the native picker
// hides it, which reads to users as "unsupported".
export const ATTACHMENT_ACCEPT =
  "image/*,application/pdf,text/plain,text/csv,application/zip,application/msword,.doc,.docx,.xlsx,.pptx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation";

/** Hidden <input type="file"> bound to the hook's picker ref. */
export function AttachmentFileInput({
  hook,
}: {
  hook: UseAttachments;
}): preact.JSX.Element {
  return (
    <input
      ref={hook.fileInputRef}
      type="file"
      multiple
      class="hidden"
      accept={ATTACHMENT_ACCEPT}
      onChange={(e) => {
        const input = e.target as HTMLInputElement;
        if (input.files && input.files.length > 0) hook.handleFiles(input.files);
        // reset so the same file can be re-picked later
        input.value = "";
      }}
    />
  );
}

/** The dashed drag-and-drop / click-to-pick zone. */
export function AttachmentDropzone({
  hook,
  fill,
}: {
  hook: UseAttachments;
  /** Stretch to fill the parent height (project upload card). */
  fill?: boolean;
}): preact.JSX.Element {
  return (
    <>
      <button
        type="button"
        onClick={hook.openPicker}
        onDragOver={(e) => {
          e.preventDefault();
          hook.setDragOver(true);
        }}
        onDragLeave={() => hook.setDragOver(false)}
        onDrop={(e) => hook.onDrop(e as unknown as DragEvent)}
        class={`w-full rounded border-2 border-dashed px-4 py-6 text-xs text-center transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
          fill ? "flex h-full flex-col items-center justify-center" : ""
        } ${
          hook.dragOver
            ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary-container)]/40 text-[color:var(--color-on-primary-container)]"
            : "border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
        }`}
        aria-label="Datei hier ablegen oder klicken zum Auswählen"
      >
        <Paperclip
          size={20}
          class="mx-auto mb-1 text-[color:var(--color-on-surface-variant)]"
          aria-hidden="true"
        />
        Datei hier ablegen oder klicken zum Auswählen
        <div class="mt-1 text-label text-[color:var(--color-on-surface-variant)]/80">
          Max. 10 MB · Bilder, PDF, Text, ZIP, DOCX, XLSX
        </div>
      </button>
      <AttachmentFileInput hook={hook} />
    </>
  );
}

/** Pending (in-flight) upload rows — shown next to / below the list. */
export function PendingRows({
  pending,
}: {
  pending: PendingUpload[];
}): preact.JSX.Element {
  return (
    <>
      {pending.map((p) => (
        <li
          key={`p-${p.localId}`}
          class="flex items-center gap-3 rounded border border-dashed border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5 text-xs opacity-80"
        >
          <span
            class="inline-flex items-center justify-center rounded bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)] shrink-0"
            style={{ width: "40px", height: "40px" }}
            aria-hidden="true"
          >
            <Paperclip size={20} />
          </span>
          <div class="flex-1 min-w-0">
            <div class="truncate text-[color:var(--color-on-surface)] font-medium">
              {p.filename}
            </div>
            <div class="text-label text-[color:var(--color-on-surface-variant)] mb-1">
              {prettyBytes(p.size)} · Lädt hoch…
            </div>
            <div
              class="h-1 w-full overflow-hidden rounded bg-[color:var(--color-surface-container-high)]"
              role="progressbar"
              aria-label="Upload-Fortschritt"
            >
              {/* Indeterminate — fetch() has no streamed upload progress. */}
              <div class="h-full w-1/3 animate-pulse bg-[color:var(--color-primary)]" />
            </div>
          </div>
        </li>
      ))}
    </>
  );
}

/**
 * The list of attachment rows with per-row hover actions (Vorschau / Download /
 * Löschen) + thumbnail. Pending rows are appended.
 *
 * - `items` is passed in (so the caller can filter, e.g. by search query).
 * - `pending` is rendered only when not filtering (caller passes [] to hide).
 * - `onDelete` comes from the hook so list + state stay in sync.
 */
export function AttachmentRows({
  items,
  pending,
  onDelete,
}: {
  items: AttachmentMeta[];
  pending: PendingUpload[];
  onDelete: (a: AttachmentMeta) => Promise<void>;
}): preact.JSX.Element {
  const me = useMe();
  const [previewOf, setPreviewOf] = useState<AttachmentMeta | null>(null);

  return (
    <ul class="flex flex-col gap-1.5">
      {items.map((a) => {
        const isImage = a.content_type.startsWith("image/");
        const canPreview = PREVIEWABLE.has(a.content_type);
        const canPreviewOffice = OFFICE_PREVIEWABLE.has(a.content_type);
        const own = me.user?.id === a.uploaded_by;
        const canDelete = own || me.user?.is_admin === true;
        return (
          <li
            key={a.id}
            class="group flex items-center gap-3 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1.5 text-xs"
          >
            {isImage ? (
              canPreview ? (
                <a
                  href={`/api/attachments/${a.id}/view`}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="shrink-0"
                  title={`${a.filename} ansehen`}
                >
                  <img
                    src={`/api/attachments/${a.id}/download`}
                    alt={a.filename}
                    class="rounded object-cover"
                    style={{ width: "40px", height: "40px" }}
                    loading="lazy"
                  />
                </a>
              ) : (
                <img
                  src={`/api/attachments/${a.id}/download`}
                  alt={a.filename}
                  class="rounded object-cover shrink-0"
                  style={{ width: "40px", height: "40px" }}
                  loading="lazy"
                />
              )
            ) : (
              <span
                class="inline-flex items-center justify-center rounded bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)] shrink-0"
                style={{ width: "40px", height: "40px" }}
                aria-hidden="true"
              >
                <Paperclip size={20} />
              </span>
            )}
            <div class="flex-1 min-w-0">
              {/* Klick auf den Namen: Vorschau, wenn der Browser das Format
                  darstellen kann (Bilder/PDF/Text via /view; Office via Modal),
                  sonst Download. */}
              {canPreview ? (
                <a
                  href={`/api/attachments/${a.id}/view`}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="block truncate font-medium text-[color:var(--color-on-surface)] hover:underline"
                  title={`${a.filename} ansehen`}
                >
                  {a.filename}
                </a>
              ) : canPreviewOffice ? (
                <button
                  type="button"
                  onClick={() => setPreviewOf(a)}
                  class="block w-full truncate text-left font-medium text-[color:var(--color-on-surface)] hover:underline"
                  title={`${a.filename} in der Vorschau ansehen`}
                >
                  {a.filename}
                </button>
              ) : (
                <a
                  href={`/api/attachments/${a.id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={a.filename}
                  class="block truncate font-medium text-[color:var(--color-on-surface)] hover:underline"
                  title={`${a.filename} herunterladen`}
                >
                  {a.filename}
                </a>
              )}
              <div class="truncate text-label text-[color:var(--color-on-surface-variant)]">
                {prettyBytes(a.size_bytes)} · {a.uploader_name} ·{" "}
                {relTime(a.created_at)}
              </div>
            </div>
            <div class="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity">
              {canPreview ? (
                <a
                  href={`/api/attachments/${a.id}/view`}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-high)] transition-colors"
                  aria-label={`${a.filename} ansehen`}
                  title="Im neuen Tab ansehen"
                >
                  <Eye size={14} aria-hidden="true" />
                </a>
              ) : canPreviewOffice ? (
                <button
                  type="button"
                  onClick={() => setPreviewOf(a)}
                  class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-high)] transition-colors"
                  aria-label={`${a.filename} in der Vorschau ansehen`}
                  title="Vorschau anzeigen"
                >
                  <Eye size={14} aria-hidden="true" />
                </button>
              ) : null}
              <a
                href={`/api/attachments/${a.id}/download`}
                target="_blank"
                rel="noopener noreferrer"
                download={a.filename}
                class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-high)] transition-colors"
                aria-label={`${a.filename} herunterladen`}
              >
                <Download size={14} aria-hidden="true" />
              </a>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => void onDelete(a)}
                  class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                  aria-label={`${a.filename} löschen`}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
            </div>
          </li>
        );
      })}

      <PendingRows pending={pending} />

      {previewOf && (
        <AttachmentPreviewModal
          attachment={{
            id: previewOf.id,
            filename: previewOf.filename,
            content_type: previewOf.content_type,
          }}
          onClose={() => setPreviewOf(null)}
        />
      )}
    </ul>
  );
}
